import React from 'react';
import { useI18n } from '../../contexts/I18n';
import { TOWNS, serviceBoxes, DAY_TO_I18N } from './utils';
import DayPicker from './DayPicker';
import SlotPicker from './SlotPicker';
import PasswordChanger from './PasswordChanger';

export default function ProfileEditForm({
  frm,
  onF,
  saveProfile,
  fErr,
  msg,
  setEdit,
  imagePreviews,
  removeImage,
  onFile,
  imageHandler,
  portfolioPreviews,
  removePortfolioImage,
  handlePortfolioFileChange,
  onSrv,
  rmSrv,
  addSrv,
  onStaff,
  rmStaff,
  addStaff,
  avail,
  daySel,
  onToggleDay,
  onSelectDay,
  onToggleSlot,
  tempPassword,
  onPasswordCleared,
}) {
  const { t } = useI18n();

  return (
    <form className="biz-edit-vert" onSubmit={saveProfile} noValidate>
      <h2>{t('ownerEditBizTitle')}</h2>
      {fErr && <p className="form-err">{fErr}</p>}
      {msg && <p className="form-ok">{msg}</p>}

      <details className="form-sec" open>
        <summary>{t('ownerBasicInfo')}</summary>
        <div className="sec-body grid-2">
          <label>
            {t('ownerLabelName')}
            <input name="name" value={frm.name} onChange={onF} required />
          </label>
          <label>
            {t('ownerPhone')}
            <input name="phone" value={frm.phone} onChange={onF} required />
          </label>
          <label>
            {t('ownerLabelTown')}
            <select name="town" value={frm.town} onChange={onF} required>
              <option value="">{t('ownerSelect')}</option>
              {TOWNS.map((town) => (
                <option key={town} value={town}>{town}</option>
              ))}
            </select>
          </label>
          <label>
            {t('ownerCategory')}
            <select name="serviceCategory" value={frm.serviceCategory} onChange={onF} required>
              <option value="">{t('ownerSelect')}</option>
              {serviceBoxes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="span-2">
            {t('ownerLabelMap')}
            <input name="mapEmbed" value={frm.mapEmbed} onChange={onF} required />
          </label>
        </div>
      </details>

      <details className="form-sec" open>
        <summary>{t('ownerImages')} ({imagePreviews.length}/5)</summary>
        <div className="sec-body">
          <div className="images-gallery">
            {imagePreviews.map((preview, idx) => (
              <div key={idx} className="image-preview">
                <img
                  src={preview}
                  alt={`Business ${idx + 1}`}
                  onError={(e) => imageHandler.handleError(e, preview)}
                  onLoad={(e) => imageHandler.handleLoad(e, preview)}
                />
                {idx > 0 && (
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(idx)}
                    title={t('removeImage')}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          {imagePreviews.length < 5 && (
            <div className="drop-box">
              <div className="drop-content">
                <span>📷</span>
                <span>{t('ownerImagesHintClick')}</span>
                <small>{t('ownerImagesHintMax')}</small>
              </div>
              <input type="file" accept="image/*" multiple onChange={onFile} />
            </div>
          )}
        </div>
      </details>

      <details className="form-sec" open>
        <summary>{t('portfolioImages')} ({portfolioPreviews.length}/30)</summary>
        <div className="sec-body">
          <div className="images-gallery">
            {portfolioPreviews.map((preview, idx) => (
              <div key={idx} className="image-preview">
                <img
                  src={preview}
                  alt={`${t('portfolioPreview')} ${idx + 1}`}
                  onError={(e) => imageHandler.handleError(e, preview)}
                  onLoad={(e) => imageHandler.handleLoad(e, preview)}
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => removePortfolioImage(idx)}
                  title={t('removePortfolioImage')}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          {portfolioPreviews.length < 30 && (
            <div className="drop-box">
              <div className="drop-content">
                <span>🖼️</span>
                <span>{t('clickToAddPortfolioImages')}</span>
                <small>{t('upTo10PortfolioImages')}</small>
              </div>
              <input type="file" accept="image/*" multiple onChange={handlePortfolioFileChange} />
            </div>
          )}
        </div>
      </details>

      <details className="form-sec" open>
        <summary>{t('ownerServices')} ({frm.services.length})</summary>
        <div className="sec-body">
          <div className="services-edit-list">
            {frm.services.map((s, i) => (
              <div key={i} className="service-edit-item">
                <div className="service-edit-inputs">
                  <input
                    name="serviceName"
                    placeholder={t('ownerServiceNamePh')}
                    value={s.serviceName}
                    onChange={(e) => onSrv(i, e)}
                    required
                  />
                  <input
                    name="price"
                    placeholder={t('ownerServicePricePh')}
                    value={s.price}
                    onChange={(e) => onSrv(i, e)}
                    required
                  />
                  <div className="duration-input-wrapper">
                    <input
                      name="duration"
                      type="number"
                      min="5"
                      max="240"
                    placeholder={t('ownerDuration')}
                    value={s.duration}
                    onChange={(e) => onSrv(i, e)}
                    required
                  />
                    <span className="duration-label">{t('ownerMins')}</span>
                  </div>
                </div>
                {frm.services.length > 1 && (
                  <button type="button" className="mini cancel" onClick={() => rmSrv(i)}>
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="mini" onClick={addSrv} style={{ marginTop: 8 }}>
            {t('ownerAddService')}
          </button>
        </div>
      </details>

      <details className="form-sec" open>
        <summary>{t('ownerStaffMembers')} ({frm.staff.length})</summary>
        <div className="sec-body">
          {frm.staff.map((s, i) => (
            <div key={s.id || i} className="staff-edit-card">
              <div className="staff-details">
                <div className="staff-inputs">
                  <input
                    name="name"
                    placeholder={t('ownerStaffNamePh')}
                    value={s.name}
                    onChange={(e) => onStaff(i, e)}
                    required
                  />
                </div>
                <div className="staff-actions">
                  {frm.staff.length > 1 && (
                    <button type="button" className="mini cancel" onClick={() => rmStaff(i)}>
                      {t('ownerRemoveStaff')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <button type="button" className="mini" onClick={addStaff} style={{ marginTop: 8 }}>
            {t('ownerAddStaff')}
          </button>
        </div>
      </details>

      <details className="form-sec" open>
        <summary>{t('ownerAvailability')}</summary>
        <div className="sec-body">
          <DayPicker
            avail={avail}
            daySel={daySel}
            onToggleDay={onToggleDay}
            onSelectDay={onSelectDay}
          />
          {daySel && (
            <>
              <h4>{t('ownerSlotsFor')} {t(DAY_TO_I18N[daySel] || daySel)}</h4>
              <SlotPicker avail={avail} daySel={daySel} onToggleSlot={onToggleSlot} />
            </>
          )}
        </div>
      </details>

      <details className="form-sec" open>
        <summary>{t('ownerChangePassword')}</summary>
        <div className="sec-body grid-2">
          <PasswordChanger tempPassword={tempPassword} onPasswordCleared={onPasswordCleared} />
        </div>
      </details>

      <div className="form-btns">
        <button type="button" className="ghost" onClick={() => setEdit(false)}>
          {t('ownerCancel')}
        </button>
        <button type="submit" className="primary">
          {t('ownerSaveChanges')}
        </button>
      </div>
    </form>
  );
}
